import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { getShareSuccessToastText } from 'utils/sentences'

import {
  selectSelectedCampaignIds,
  selectTimeFilter,
} from 'containers/Dashboard/Awareness/selectors'
import { selectWithoutScanback } from 'containers/Dashboard/Home/selectors'
import {
  INVESTIGATION_SHARE_CSV_PAGE_SIZE,
  POC_PAGE_SIZE,
} from 'containers/Investigation/ThreatLog/constants'
import { selectThreatLogFilters } from 'containers/Investigation/ThreatLog/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { addToast } from '../Toasts/actions'

import {
  getSharingEmailsError,
  getSharingEmailsSuccess,
  shareReset,
  shareSubmitError,
  shareSubmitSuccess,
} from './actions'
import {
  FETCH_SHARING_EMAILS_ASYNC,
  PDF_OPTION_CONST,
  SHARE_SUBMIT_ASYNC,
  SHARE_TYPE,
} from './constants'

const fetchSharingEmailsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_SHARING_EMAILS_ASYNC.INITIATE).mergeMap((action) =>
    api.mailboxes
      .getEmails(
        store.getState().partnerOptions.selectedCompany?.value,
        action.payload.search,
        action.payload.includeMspUsers
      )
      .map((response) => getSharingEmailsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(getSharingEmailsError, error)))
  )

const shareSubmitEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SHARE_SUBMIT_ASYNC.INITIATE).mergeMap((action) => {
    let res
    let dateFilter
    let campaignIds
    let reportTimes
    let mailboxesFilters
    let filters
    const companyFilterValue = selectCompanyFilter(store.getState())?.value
    let state = store.getState()
    const fileType = state.shareReport.fileType
    const body = {
      message: state.shareReport.message,
      email: state.shareReport.selectedProfilesProperties,
      is_pdf: fileType === PDF_OPTION_CONST,
    }
    switch (action.payload.shareType) {
      case SHARE_TYPE.REPORT.THREAT_PROTECTION:
        dateFilter = state.dashboard.threatProtection.flow.dateFilter

        res = api.accounts.sendThreatProtectionReport(companyFilterValue, {
          ...body,
          withoutScanBack: selectWithoutScanback(state),
          feDateRange: getRangeFromDateFilter(dateFilter),
        })
        break
      case SHARE_TYPE.REPORT.SCANBACK:
        res = api.accounts.sendScanbackReport(companyFilterValue, { ...body })
        break

      case SHARE_TYPE.REPORT.AWARENESS:
        res = api.accounts.sendCampaignReport(
          {
            ...body,
            campaign_id: state.awarenessCenter.campaignReport.data.id,
          },
          !!state.app.data.partner
        )
        break
      case SHARE_TYPE.REPORT.PHISHING_SIMULATOR:
        state = store.getState()
        dateFilter = getRangeFromDateFilter(selectTimeFilter(state))
        campaignIds = selectSelectedCampaignIds(state)
        reportTimes = action.payload.customData
        res = api.accounts.sendPhishingSimulatorReport(companyFilterValue, {
          ...body,
          feDateRange: dateFilter,
          campaignIds,
          reportTimes,
        })
        break
      case SHARE_TYPE.REPORT.REPORTED_INCIDENTS:
        res = api.accounts.shareReportedIncidentsReport(companyFilterValue, body)
        break
      case SHARE_TYPE.REPORT.ANOMALIES:
        dateFilter = state.dashboard.threatProtection.list.dateFilter
        const banner_type = state.dashboard.threatProtection.list.bannerTypeFilter.map(
          (elem) => elem.value
        )
        const search = state.dashboard.threatProtection.list.search
        res = api.accounts.shareAnomaliesReport(companyFilterValue, {
          ...body,
          feDateRange: getRangeFromDateFilter(dateFilter),
          banner_type,
          search,
        })
        break
      case SHARE_TYPE.REPORT.INCIDENT_SUMMARY_INCIDENTS:
        res = api.dashboard.getThreatProtectionIncidentsList(
          companyFilterValue,
          false,
          true,
          state.dashboard.threatProtection.list.page || 1,
          getRangeFromDateFilter(state.dashboard.threatProtection.list.dateFilter),
          state.dashboard.threatProtection.list.search,
          state.dashboard.threatProtection.list.incidentTypeFilter?.map((elem) => elem.label),
          state.dashboard.threatProtection.list.tagsFilter?.map((elem) => elem.label),
          state.dashboard.threatProtection.list.departmentFilter?.map((elem) => elem.label),
          state.dashboard.threatProtection.list.resolvedByFilter?.map((elem) => elem.label),
          state.dashboard.threatProtection.list.reportedByFilter?.map((elem) => elem.label),
          state.dashboard.threatProtection.list.resolutionTypeFilter?.map((elem) => elem.label),
          state.dashboard.threatProtection.list.threatTypeFilter?.map((elem) => elem.label),
          state.shareReport.selectedProfilesProperties,
          state.shareReport.message
        )
        break
      case SHARE_TYPE.REPORT.REMEDIATED_EMAILS:
        dateFilter = state.resolutioncenter.remediatedIncidents.dateFilter
        res = api.accounts.shareRemediatedEmailsReport(companyFilterValue, {
          ...body,
          shareCSV: true,
          feDateRange: getRangeFromDateFilter(dateFilter),
        })
        break
      case SHARE_TYPE.REPORT.REPORT_MAILBOXES:
        mailboxesFilters = {
          typeFilter: state.mailboxes.management.typeFilter,
          tagsFilter: state.mailboxes.management.tagsFilter.map((elem) => elem.value).join(','),
          segmentsFilter: state.mailboxes.management.segmentsFilter
            .map((elem) => elem.value)
            .join(','),
          protectedFilter: state.mailboxes.management.protectedFilter,
          riskLevelsFilter: state.mailboxes.management.riskLevelsFilter
            .map((elem) => elem.value)
            .join(','),
        }
        res = api.accounts.shareMailboxesReport(companyFilterValue, body, mailboxesFilters)
        break
      case SHARE_TYPE.REPORT.THREAT_LOG_REPORT:
        {
          filters = selectThreatLogFilters(state)
          body.page_size = INVESTIGATION_SHARE_CSV_PAGE_SIZE
          const { tagIds, ...restFilters } = filters
          res = api.threatLog.shareLogsCSV(restFilters, { ...body, tagIds })
        }
        break
      case SHARE_TYPE.REPORT.INVESTIGATION_REPORT:
        {
          filters = selectThreatLogFilters(state)
          body.page_size = INVESTIGATION_SHARE_CSV_PAGE_SIZE
          filters.page_number = 1
          const { tagIds, ...restFilters } = filters
          res = api.threatLog.shareInvestigationCSV(restFilters, { ...body, tagIds })
        }
        break
      case SHARE_TYPE.REPORT.POC_REPORT_LAST_LOG:
        {
          filters = selectThreatLogFilters(state)
          filters.page_size = POC_PAGE_SIZE
          filters.page_number = 1
          const { tagIds, ...restFilters } = filters
          res = api.threatLog.shareInvestigationPocCSV(restFilters, { ...body, tagIds })
        }
        break
      default:
        throw new Error('Not supported SHARE_TYPE')
    }
    return res
      .mergeMap((response) =>
        Observable.of(
          shareSubmitSuccess(response),
          addToast({
            message: getShareSuccessToastText(action.payload.shareType),
          }),
          shareReset()
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(shareSubmitError, error),
          addToast({
            message: 'We could not share this file, please try again or contact CS.',
            type: 'error',
          })
        )
      )
  })

export default combineEpics(shareSubmitEpic, fetchSharingEmailsEpic)
